import type { Dtos } from "@inrev/common";
import { formatToDollar } from "@inrev/common";
import { Option } from "@inrev/core";
import { FC } from "react";
import { StatBlock } from "./StatBlock";
import { formatDays } from "./utils/formatDays";

type Report = Dtos.Admin.SuretyAccount.Get.Response["companies"][number]["creditReports"][number];

type TradelinesPanelProps = {
	report: Report;
};

export const TradelinesPanel: FC<TradelinesPanelProps> = ({ report }) => {
	const elements = report.creditProfile?.experianExecutiveElements;
	const active = elements?.activeTradelineCount;
	const totalTradelines = elements?.allTradelineCount;
	const currentBal = elements?.currentAccountBalance;
	const allTimeBal = elements?.allTradelineBalance;
	const currentDbt = elements?.currentDbt;
	const avgDbt = elements?.monthlyAverageDbt;
	const highDbt6 = elements?.highestDbt6Months;
	const highDbt5q = elements?.highestDbt5Quarters;
	const singleHigh = elements?.singleLineHighCredit;
	const avgBal5q = elements?.averageBalance5Quarters;
	const low6 = elements?.lowBalance6Months;
	const high6 = elements?.highBalance6Months;
	const inquiries = elements?.inquiriesPrevious9Months;

	const summaryParts: string[] = [];
	if (Option.isSome(active)) {
		summaryParts.push(`${active} active`);
	}
	if (Option.isSome(currentBal)) {
		summaryParts.push(`${formatToDollar(currentBal, true)} balance`);
	}

	return (
		<div
			className="space-y-3 border-b border-bc-default pb-5"
			data-amp-mask
			data-testid="company_credit_tradelines_panel"
		>
			<div className="flex flex-wrap items-baseline justify-between gap-2">
				<h3 className="text-[15px] font-medium text-primary">Tradelines & DBT</h3>
				{summaryParts.length > 0 && (
					<span className="text-secondary text-[13px]">{summaryParts.join(" · ")}</span>
				)}
			</div>
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<StatBlock
					label="Active Tradelines"
					value={active}
					sub={Option.isSome(totalTradelines) ? `${totalTradelines} total` : undefined}
				/>
				<StatBlock
					label="Current Balance"
					value={Option.isSome(currentBal) ? formatToDollar(currentBal, true) : undefined}
					sub={
						Option.isSome(allTimeBal) ? `${formatToDollar(allTimeBal, true)} all-time` : undefined
					}
				/>
				<StatBlock
					label="Current DBT"
					value={formatDays(currentDbt)}
					sub={Option.isSome(avgDbt) ? `${formatDays(avgDbt)} avg` : undefined}
				/>
				<StatBlock
					label="High DBT (6mo)"
					value={formatDays(highDbt6)}
					sub={Option.isSome(highDbt5q) ? `${formatDays(highDbt5q)} (5 qtr)` : undefined}
				/>
				<StatBlock
					label="Single-Line High Credit"
					value={Option.isSome(singleHigh) ? formatToDollar(singleHigh, true) : undefined}
				/>
				<StatBlock
					label="Avg Balance (5Q)"
					value={Option.isSome(avgBal5q) ? formatToDollar(avgBal5q, true) : undefined}
				/>
				<StatBlock
					label="Balance Range (6mo)"
					value={
						Option.isSome(low6) && Option.isSome(high6)
							? `${formatToDollar(low6, true)} — ${formatToDollar(high6, true)}`
							: undefined
					}
				/>
				<StatBlock label="Inquiries (9mo)" value={inquiries} />
			</div>
		</div>
	);
};
